import { useAuth } from "@tellescope/tanstack-query";
import { useMemo } from "react";

export interface UseFromNumbersArgs {
  /**
   * The context-preferred "from" number to select by default, if available — e.g. the recipient
   * enduser's `defaultFromPhone` when composing an SMS, or the org's configured default phone number
   * when placing a call. Takes precedence over every fallback in the default-selection chain.
   */
  preferredNumber?: string;
}

export interface UseFromNumbers {
  /** Available "from" numbers to choose from, in priority order (deduplicated). */
  options: string[];
  /** The number to select by default; `""` when neither the user nor the org has any number configured. */
  defaultFromNumber: string;
}

/**
 * Resolve the "From" number options and default selection for outbound SMS and calls.
 *
 * Options (priority):
 * 1. If the user's `availableFromNumbers` allow-list is present and non-empty, use exactly that list.
 * 2. Otherwise build from the user's `twilioNumber`, then the org's `twilioNumber`, then the org's
 *    additional `twilioNumbers` (deduplicated).
 *
 * Default selected number:
 *   `preferredNumber`
 *     -> `availableFromNumbers[0]`
 *     -> user's `twilioNumber`
 *     -> org's `twilioNumber`
 *     -> `""` (none available)
 *
 * An empty `availableFromNumbers` never blanks the picker — it falls back to the user and org numbers.
 * The picker is only empty when neither the user nor the org has any number configured. This does not
 * apply to replies within an existing SMS thread, where the "from" number is locked to the thread's number.
 */
export const useFromNumbers = ({ preferredNumber }: UseFromNumbersArgs = {}): UseFromNumbers => {
  const { session, selectedOrganization: organization } = useAuth();

  return useMemo(() => {
    const userInfo = session?.userInfo;
    const userNumber = userInfo?.twilioNumber;
    const orgNumber = organization?.twilioNumber;

    const fallbackNumbers = [userNumber, orgNumber, ...(organization?.twilioNumbers ?? [])].filter(
      (number): number is string => !!number
    );
    const options = userInfo?.availableFromNumbers?.length
      ? userInfo.availableFromNumbers
      : [...new Set(fallbackNumbers)];

    const defaultFromNumber = preferredNumber || userInfo?.availableFromNumbers?.[0] || userNumber || orgNumber || "";

    return { options, defaultFromNumber };
  }, [session?.userInfo, organization?.twilioNumber, organization?.twilioNumbers, preferredNumber]);
};
